import { useState, useEffect, useMemo } from "react";
import { ArrowRight, Search, X as CloseIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../../store/slices/authSlice";

import styles from "./RawMaterials.module.css";
import RawMaterialCard from "./RawMaterialCard";
import RawMaterialDetailModal from "./RawMaterialDetailModal";

const ALL = "All";

export default function RawMaterials({ limit = null }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");
  const isAuthenticated = !!localStorage.getItem("token");
  const navigate = useNavigate();

  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState(ALL);

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const handleAction = (callbackAction) => {
    if (!isAuthenticated) {
      navigate("/login");
    } else {
      callbackAction();
    }
  };

  const fetchData = async (pageNum = 1) => {
    try {
      const response = await api.get(`/materials?page=${pageNum}&per_page=20`);
      if (response.data.code === 200) {
        setMaterials((prev) =>
          pageNum === 1 ? response.data.data : [...prev, ...response.data.data],
        );
        setPagination(response.data.pagination || null);
      }
    } catch (error) {
      console.error("Fetch Error:", error.response?.data || error.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setPage(1);
    fetchData(1);
  }, [i18n.language]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    setPage(nextPage);
    fetchData(nextPage);
  };

  const categoriesList = useMemo(() => {
    if (!materials || materials.length === 0) return [ALL];
    const allNames = materials.flatMap(
      (m) => m.categories?.map((cat) => cat.name) || [],
    );
    return [ALL, ...new Set(allNames)];
  }, [materials]);

  const filteredAll = useMemo(() => {
    const q = search.toLowerCase();
    return materials.filter((m) => {
      const matchCat =
        activeCat === ALL ||
        m.categories?.some((cat) => cat.name === activeCat);

      const matchSearch =
        !q ||
        m.name.toLowerCase().includes(q) ||
        m.cas_number?.toLowerCase().includes(q);

      return matchCat && matchSearch;
    });
  }, [materials, search, activeCat]);

  const filtered = useMemo(
    () => (limit ? filteredAll.slice(0, limit) : filteredAll),
    [filteredAll, limit],
  );

  const hasMore = limit && filteredAll.length > limit;

  const canLoadMore =
    !limit && pagination && pagination.current_page < pagination.last_page;

  return (
    <section className={styles.section} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <span className={styles.badge}>{t("marketplace.badge")}</span>
            <h2 className={styles.title}>{t("marketplace.title")}</h2>
            <p className={styles.subtitle}>{t("marketplace.subtitle")}</p>
          </div>
        </div>

        <div className={styles.searchBox}>
          <Search size={18} />
          <input
            type="text"
            placeholder={t("marketplace.searchPlaceholder")}
            value={search}
            onFocus={() => !isAuthenticated && navigate("/login")}
            onChange={(e) => isAuthenticated && setSearch(e.target.value)}
          />
          {search && (
            <button
              className={styles.clearBtn}
              onClick={() => handleAction(() => setSearch(""))}
            >
              <CloseIcon size={14} />
            </button>
          )}
        </div>

        <div className={styles.filters}>
          {categoriesList.map((cat) => (
            <button
              key={cat}
              className={`${styles.filterBtn} ${activeCat === cat ? styles.active : ""}`}
              onClick={() => handleAction(() => setActiveCat(cat))}
            >
              {cat === ALL ? t("marketplace.all") : cat}
              {cat !== ALL && (
                <span className={styles.filterCount}>
                  {
                    materials.filter((m) =>
                      m.categories?.some((c) => c.name === cat),
                    ).length
                  }
                </span>
              )}
            </button>
          ))}
        </div>

        {!loading && (
          <p className={styles.resultsCount}>
            {filteredAll.length} {t("marketplace.resultsFound")}
          </p>
        )}

        {loading ? (
          <div className={styles.loadingWrap}>
            {[...Array(limit || 8)].map((_, i) => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className={styles.grid}>
            {filtered.map((material) => (
              <RawMaterialCard
                key={material.id}
                material={material}
                onClick={() =>
                  handleAction(() => setSelectedMaterial(material))
                }
              />
            ))}
          </div>
        ) : !isAuthenticated ? (
          <div className={styles.guestBox}>
            <div className={styles.guestIcon}>🧪</div>
            <h3>{t("marketplace.guestTitle")}</h3>
            <p>{t("marketplace.guestDescription")}</p>
            <span>{t("marketplace.guestUnlock")}</span>
            <button
              className={styles.loginBtn}
              onClick={() => navigate("/login")}
            >
              {t("marketplace.loginBtn")}
            </button>
          </div>
        ) : (
          <div className={styles.empty}>
            <Search size={48} strokeWidth={1.2} />
            <p>{t("marketplace.noMaterials")}</p>
            <span>{t("marketplace.tryDifferent")}</span>
          </div>
        )}

        {hasMore && (
          <div className={styles.viewMoreWrap}>
            <button
              className={styles.viewMoreBtn}
              onClick={() => handleAction(() => navigate("/raw-materials"))}
            >
              {t("marketplace.viewAll")}
              <ArrowRight
                size={16}
                style={{ transform: isRtl ? "rotate(180deg)" : "none" }}
              />
            </button>
          </div>
        )}

        {canLoadMore && (
          <div className={styles.loadMoreWrap}>
            <button
              className={styles.loadMoreBtn}
              onClick={() => handleAction(handleLoadMore)}
              disabled={loadingMore}
            >
              {loadingMore ? "..." : t("common.loadMore")}
            </button>
          </div>
        )}
      </div>

      {selectedMaterial && (
        <RawMaterialDetailModal
          material={selectedMaterial}
          onClose={() => setSelectedMaterial(null)}
        />
      )}
    </section>
  );
}
